import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Link } from 'react-router-dom'
import { APP_AGENT_API_GUIDE_TITLE, APP_CONSOLE_NAME, APP_NAME } from '../constants/brand'
import { useTranslation } from 'react-i18next'

// KEEP IN SYNC with docs/agent-api-guide.md — this constant is a hand-copied
// clone of that document (CLAUDE.md #4: no silent duplicate sources of truth).
// Any edit here must be mirrored there, and vice versa.
const AGENT_API_GUIDE = `# ${APP_AGENT_API_GUIDE_TITLE}

REST API reference for programmatic agents interacting with ${APP_NAME}. Covers the full workflow: authenticate, talk to the Director, download generated artifacts, and manage projects. All creative work (creating characters, locations, videos, etc.) goes through the Director via natural language.

**Base URL:** \`https://api.dreamwrite.com\`

---

## Authentication

All endpoints require an API key passed as a Bearer token.

An admin creates an API key on the **\`/admin/api\`** page in the ${APP_CONSOLE_NAME} (also reachable from the user menu as "Manage API keys"). Keys belong to the account that created them.

\`\`\`
Authorization: Bearer dwk_your_api_key_here
\`\`\`

### Two things that will break a REST client in production

**1. Session affinity — send the \`DREAMWRIGHT_PROJECT\` cookie.** The API runs as
multiple instances behind a load balancer configured for \`HTTP_COOKIE\` affinity on
\`DREAMWRIGHT_PROJECT\`. The console sets that cookie in the browser; a curl/HTTP
client sends nothing, gets round-robined, and hits a different instance on each
call. Director orchestrators are per-process, so multi-turn state splits across
instances and you get 409s. Always carry a cookie jar **and** set the cookie
explicitly:

\`\`\`bash
BASE="https://api.dreamwrite.com"   # or your API base
PROJECT_ID="your-project-id"
JAR="$(mktemp)"
curl -sS -X POST "$BASE/projects/$PROJECT_ID/agentic/agent/chat" \\
  -H "Authorization: Bearer $TOKEN" -H "Content-Type: application/json" \\
  -b "$JAR" -c "$JAR" -b "DREAMWRIGHT_PROJECT=$PROJECT_ID" \\
  -d '{"prompt": "..."}'
\`\`\`

**2. Chat before tools — a cold instance 404s \`/agentic/director/tools/*\`.** The chat path
creates the project workspace directory if it is missing; the \`/agentic/director/tools/*\`
routes do not — they raise \`404 projectNotFound\`. So a client whose *first* call is
a tool call gets a 404 even though its key and project access are both valid. Send
one chat message first, then call tools on the same instance (same cookie jar).

---

## 1. Project Lifecycle

| Method | Endpoint | Purpose |
|--------|----------|---------|
| \`POST\` | \`/projects\` | Create a new project |
| \`GET\` | \`/projects/{projectId}\` | Get project details |
| \`GET\` | \`/projects/{projectId}/full\` | Get full project with characters, locations, chapters |
| \`DELETE\` | \`/projects/{projectId}\` | Delete project |

### Create a project

\`\`\`bash
curl -X POST $BASE_URL/projects \\
  -H "Authorization: Bearer $TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"name": "My Fantasy Story", "description": "An epic tale"}'
\`\`\`

---

## 2. Director Chat

The Director is the AI that interprets your creative prompts, creates assets (characters, locations, videos, etc.), and manages the production pipeline.

| Method | Endpoint | Purpose |
|--------|----------|---------|
| \`POST\` | \`/projects/{projectId}/agentic/agent/chat\` | **Send a message, block until Director responds** (recommended) |
| \`POST\` | \`/projects/{projectId}/agentic/agent/connect\` | Send a prompt (non-blocking, returns immediately) |
| \`POST\` | \`/projects/{projectId}/agentic/agent/respond\` | Answer the Director's pending questions |
| \`POST\` | \`/projects/{projectId}/agentic/cancel-node\` | Cancel a running request |
| \`GET\` | \`/projects/{projectId}/agentic/agent/state\` | Poll current state (messages, status, pending questions) |

### Recommended: Synchronous chat (\`/agent/chat\`)

This endpoint blocks until the Director finishes (or times out). It auto-confirms cost and generation prompts so the Director can run unattended.

\`\`\`bash
curl -X POST $BASE_URL/projects/$PROJECT_ID/agentic/agent/chat \\
  -H "Authorization: Bearer $TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "prompt": "Create a fantasy hero character named Aria with silver armor",
    "style": "anime_cel_shading",
    "production_target": "video",
    "timeout": 120,
    "auto_confirm_cost": true
  }'
\`\`\`

**Request fields:**

| Field | Type | Required | Description |
|-------|------|----------|-------------|
| \`prompt\` | string | Yes | Message to send to the Director |
| \`production_target\` | string | No | \`"video"\` or \`"webtoon"\` (first message only) |
| \`style\` | string | No | Visual style ID (see [Styles](#7-styles)) |
| \`timeout\` | int | No | Max seconds to wait (default: 120, max: 600) |
| \`auto_confirm_cost\` | bool | No | Auto-confirm cost prompts (default: true) |
| \`referenced_node_ids\` | string[] | No | Node IDs to reference in context |

**Response:**

\`\`\`json
{
  "ok": true,
  "status": "idle",
  "new_messages": [
    {
      "role": "assistant",
      "content": "I've created a Character Designer for Aria..."
    }
  ],
  "pending_questions": [],
  "last_error": null
}
\`\`\`

**Status values:** \`idle\` (done), \`running\` (timed out but still working), \`pending_questions\` (needs answers), \`error\`.

### Alternative: Non-blocking flow

For long-running operations, use the non-blocking pattern:

1. \`POST /agent/connect\` with \`{ "prompt": "..." }\` — returns immediately with \`{ "status": "running" }\`
2. Poll \`GET /agent/state\` every 1-2 seconds until \`status !== "running"\`
3. Read \`messages[]\` for the Director's response
4. If \`pending_questions\` is non-empty, answer with \`POST /agent/respond\`

### Answering Director questions

When the Director asks questions (e.g., style choices, creative direction), the status becomes \`pending_questions\`. Each question has an \`id\` and a list of options:

\`\`\`bash
curl -X POST $BASE_URL/projects/$PROJECT_ID/agentic/agent/respond \\
  -H "Authorization: Bearer $TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "answers": {
      "<question_id>": {
        "selected_option": "option_1",
        "custom_text": null
      }
    }
  }'
\`\`\`

The \`answers\` map uses each question's \`id\` from the \`pending_questions\` array as the key.

---

## 3. Conversation State

| Method | Endpoint | Purpose |
|--------|----------|---------|
| \`GET\` | \`/projects/{projectId}/agentic/agent/state\` | Get full conversation state including all messages |

### Reading the current state

\`\`\`bash
curl $BASE_URL/projects/$PROJECT_ID/agentic/agent/state \\
  -H "Authorization: Bearer $TOKEN"
\`\`\`

**Response:**

\`\`\`json
{
  "status": "idle",
  "messages": [
    { "role": "user", "content": "Create a hero character" },
    { "role": "assistant", "content": "I've created a Character Designer..." }
  ],
  "pending_questions": [],
  "last_error": null
}
\`\`\`

---

## 4. What the Director Can Create

The Director creates and manages these asset types on your behalf. You interact with them through natural language prompts.

| Asset Type | What It Produces | Example Prompt |
|------------|-----------------|----------------|
| **Character Designer** | Character reference sheet (three-view image), voice profile | "Create a warrior princess named Aria with silver armor" |
| **Location Designer** | Environment/background image | "Design a medieval castle at sunset with storm clouds" |
| **Object Designer** | Prop/object reference image | "Create an enchanted sword with blue runes" |
| **Script Writer** | Screenplay/script text | "Write a 3-scene script about Aria's quest" |
| **Storyboard Designer** | Panel-by-panel storyboard images | "Create a storyboard from the script with 9 panels" |
| **Image Generator** | Standalone image from text | "Generate a wide shot of the castle exterior" |
| **Video Generator** | Video clip from text or image | "Generate a video of Aria walking through the gates" |
| **Music Generator** | Background music/soundtrack | "Create epic orchestral music, 30 seconds" |
| **Speech Generator** | Voiceover/speech audio | "Generate Aria's dialogue for scene 1" |
| **Video Editor** | Combined/edited video with audio | "Combine the video clips with the music track" |

---

## 5. Artifact Access

Generated images, videos, and audio can be downloaded via their paths (returned in the Director's messages).

| Method | Endpoint | Purpose |
|--------|----------|---------|
| \`GET\` | \`/projects/{projectId}/outputs/{path}\` | Download any generated file (redirects to signed URL) |
| \`GET\` | \`/projects/{projectId}/assets\` | List all assets (filterable by type) |
| \`GET\` | \`/projects/{projectId}/assets/{type}/{assetId}\` | Get asset file |
| \`GET\` | \`/projects/{projectId}/assets/{type}/{assetId}/metadata\` | Get asset metadata |
| \`GET\` | \`/projects/{projectId}/agentic/nodes/{nodeId}/history\` | Get generation history for a node |
| \`POST\` | \`/projects/{projectId}/agentic/nodes/{nodeId}/pick\` | Select a specific generation from history |

### Downloading an artifact

The Director's messages include file paths for generated assets. Use them to download:

\`\`\`bash
curl -L $BASE_URL/projects/$PROJECT_ID/outputs/<path_from_director_message> \\
  -H "Authorization: Bearer $TOKEN" \\
  -o output.jpg
\`\`\`

### Listing assets

\`\`\`bash
# List all image assets
curl "$BASE_URL/projects/$PROJECT_ID/assets?type=image" \\
  -H "Authorization: Bearer $TOKEN"

# List all video assets
curl "$BASE_URL/projects/$PROJECT_ID/assets?type=video" \\
  -H "Authorization: Bearer $TOKEN"
\`\`\`

### Browsing generation history

Each generated asset keeps a history of all generations. You can browse past versions and select one:

\`\`\`bash
# Get all generations for a node
curl $BASE_URL/projects/$PROJECT_ID/agentic/nodes/$NODE_ID/history \\
  -H "Authorization: Bearer $TOKEN"
\`\`\`

**Response:**

\`\`\`json
{
  "node_id": "n_abc123",
  "generations": [
    {
      "user_prompt": "A warrior in silver armor",
      "metadata": { "style": "anime_cel_shading" }
    },
    {
      "user_prompt": "A warrior in gold armor",
      "metadata": { "style": "anime_cel_shading" }
    }
  ],
  "picked_index": 1,
  "total_count": 2
}
\`\`\`

### Selecting a generation

\`\`\`bash
curl -X POST $BASE_URL/projects/$PROJECT_ID/agentic/nodes/$NODE_ID/pick \\
  -H "Authorization: Bearer $TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "target_node_id": "n_abc123",
    "generation_index": 0
  }'
\`\`\`

---

## 6. Reference Image Upload

Upload images to use as references when creating characters, locations, or other assets.

| Method | Endpoint | Purpose |
|--------|----------|---------|
| \`POST\` | \`/projects/{projectId}/assets/upload\` | Upload reference image (PNG/JPEG/WebP, max 10MB) |

\`\`\`bash
curl -X POST $BASE_URL/projects/$PROJECT_ID/assets/upload \\
  -H "Authorization: Bearer $TOKEN" \\
  -F "file=@reference.png" \\
  -F "name=hero-reference"
\`\`\`

After uploading, tell the Director to use it:

\`\`\`bash
curl -X POST $BASE_URL/projects/$PROJECT_ID/agentic/agent/chat \\
  -H "Authorization: Bearer $TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "prompt": "Create a character based on the uploaded reference image",
    "timeout": 120,
    "auto_confirm_cost": true
  }'
\`\`\`

---

## 7. Styles

Visual styles control the artistic look of all generated images and videos.

| Method | Endpoint | Purpose |
|--------|----------|---------|
| \`GET\` | \`/styles\` | List all available styles |
| \`GET\` | \`/styles/{styleId}\` | Get style details |

\`\`\`bash
curl $BASE_URL/styles \\
  -H "Authorization: Bearer $TOKEN"
\`\`\`

Pass the style \`id\` when chatting with the Director:

\`\`\`json
{ "prompt": "Create a hero", "style": "anime_cel_shading" }
\`\`\`

---

## 8. Credits & Cost

| Method | Endpoint | Purpose |
|--------|----------|---------|
| \`GET\` | \`/sparks\` | Check your credit balance |
| \`POST\` | \`/sparks/estimates\` | Estimate cost before generating |
| \`GET\` | \`/projects/{projectId}/agentic/cost-summary\` | Get cumulative cost for a project |

### Checking your balance

\`\`\`bash
curl $BASE_URL/sparks \\
  -H "Authorization: Bearer $TOKEN"
\`\`\`

---

## Complete Workflow Example

\`\`\`bash
TOKEN="dwk_your_api_key"
BASE="https://api.dreamwrite.com"

# 1. Create a project
PROJECT_ID=$(curl -s -X POST $BASE/projects \\
  -H "Authorization: Bearer $TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"name": "Hero Story"}' | jq -r '.id')

# 2. Tell the Director what to create (blocks until done)
curl -X POST $BASE/projects/$PROJECT_ID/agentic/agent/chat \\
  -H "Authorization: Bearer $TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "prompt": "Create a fantasy story about a warrior princess named Aria. Generate a character for her and a castle location.",
    "style": "anime_cel_shading",
    "production_target": "video",
    "timeout": 300,
    "auto_confirm_cost": true
  }'

# 3. Ask the Director to create a video
curl -X POST $BASE/projects/$PROJECT_ID/agentic/agent/chat \\
  -H "Authorization: Bearer $TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "prompt": "Now generate a video of Aria walking through the castle gates at sunset",
    "timeout": 300,
    "auto_confirm_cost": true
  }'

# 4. Review what was created
curl $BASE/projects/$PROJECT_ID/agentic/agent/state \\
  -H "Authorization: Bearer $TOKEN" | jq '.messages'

# 5. List generated assets
curl "$BASE/projects/$PROJECT_ID/assets?type=image" \\
  -H "Authorization: Bearer $TOKEN"

# 6. Download artifacts (use paths from Director messages or asset listing)
curl -L "$BASE/projects/$PROJECT_ID/outputs/<image_path>" \\
  -H "Authorization: Bearer $TOKEN" \\
  -o character_sheet.jpg

curl -L "$BASE/projects/$PROJECT_ID/outputs/<video_path>" \\
  -H "Authorization: Bearer $TOKEN" \\
  -o video.mp4
\`\`\`

---

## Endpoint Summary

| # | Category | Count |
|---|----------|-------|
| 1 | Project Lifecycle | 4 |
| 2 | Director Chat | 5 |
| 3 | Conversation State | 1 |
| 4 | Asset Types | (reference) |
| 5 | Artifact Access | 6 |
| 6 | Reference Upload | 1 |
| 7 | Styles | 2 |
| 8 | Credits & Cost | 3 |
| | **Total** | **~22** |
`

export default function ForAgentsPage() {
  const { t } = useTranslation('pages')
  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-4xl mx-auto px-6 py-16">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-text-muted text-sm no-underline hover:text-foreground transition-colors mb-8"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          {t('forAgents.backToHome')}
        </Link>

        <article className="agent-api-guide">
          <Markdown
            remarkPlugins={[remarkGfm]}
            components={{
              h1: ({ children }) => (
                <h1 className="text-display-sm font-bold mb-6 pb-3 border-b border-border">{children}</h1>
              ),
              h2: ({ children }) => (
                <h2 className="text-2xl font-semibold mt-10 mb-4 pb-2 border-b border-border">{children}</h2>
              ),
              h3: ({ children }) => (
                <h3 className="text-lg font-semibold mt-6 mb-3">{children}</h3>
              ),
              p: ({ children }) => (
                <p className="mb-4 leading-relaxed text-text-secondary">{children}</p>
              ),
              a: ({ href, children }) => (
                <a href={href} className="text-accent hover:underline">{children}</a>
              ),
              ul: ({ children }) => (
                <ul className="mb-4 pl-6 list-disc space-y-1 text-text-secondary">{children}</ul>
              ),
              ol: ({ children }) => (
                <ol className="mb-4 pl-6 list-decimal space-y-1 text-text-secondary">{children}</ol>
              ),
              li: ({ children }) => (
                <li className="leading-relaxed">{children}</li>
              ),
              code: ({ className, children, ...props }) => {
                const isBlock = className?.startsWith('language-')
                if (isBlock) {
                  return (
                    <code className={`block ${className || ''}`} {...props}>
                      {children}
                    </code>
                  )
                }
                return (
                  <code className="bg-surface-2 text-accent px-1.5 py-0.5 rounded text-sm" {...props}>
                    {children}
                  </code>
                )
              },
              pre: ({ children }) => (
                <pre className="bg-surface-2 border border-border rounded-lg p-4 mb-4 overflow-x-auto text-sm leading-relaxed">
                  {children}
                </pre>
              ),
              table: ({ children }) => (
                <div className="overflow-x-auto mb-4">
                  <table className="w-full border-collapse text-sm">
                    {children}
                  </table>
                </div>
              ),
              thead: ({ children }) => (
                <thead className="bg-surface-2">{children}</thead>
              ),
              th: ({ children }) => (
                <th className="border border-border px-3 py-2 text-left font-semibold text-foreground">{children}</th>
              ),
              td: ({ children }) => (
                <td className="border border-border px-3 py-2 text-text-secondary">{children}</td>
              ),
              hr: () => (
                <hr className="border-border my-8" />
              ),
              strong: ({ children }) => (
                <strong className="font-semibold text-foreground">{children}</strong>
              ),
              blockquote: ({ children }) => (
                <blockquote className="border-l-4 border-accent pl-4 my-4 text-text-muted italic">{children}</blockquote>
              ),
            }}
          >
            {AGENT_API_GUIDE}
          </Markdown>
        </article>
      </div>
    </div>
  )
}
